import React, { useMemo, useRef, useState } from "react";
import { Check, Search, X } from "lucide-react";

// Missions récurrentes de la fiche client et leurs codes dans la liste
// « Missions » des Paramètres KYC/LAB (demande d'Anthony du 19/10/2026).
// Plusieurs codes possibles par mission ; sans code saisi, la mission prend
// son code par défaut (même règle que le backend, services/codesMission.js).
// Codes modifiables par les Administrateurs et Experts-comptables.

export const MISSIONS_CODEES = [
  { champ: "missionComptable", codes: "codesMissionComptable", defaut: "M001", libelle: "Comptable" },
  { champ: "missionSociale", codes: "codesMissionSociale", defaut: "M021", libelle: "Sociale" },
  { champ: "missionJuridique", codes: "codesMissionJuridique", defaut: "M031", libelle: "Juridique" },
];
export const ROLES_CODES_MISSION = ["ADMINISTRATEUR", "EXPERT_COMPTABLE"];

const estCochee = (client, m) => String(client?.[m.champ] || "").toLowerCase() === "oui";
const codesSaisis = (client, m) => (Array.isArray(client?.[m.codes]) ? client[m.codes].filter(Boolean) : []);
export const codesEffectifs = (client, m) => (!estCochee(client, m) ? [] : codesSaisis(client, m).length ? codesSaisis(client, m) : [m.defaut]);

const NIVEAUX = {
  faible: { label: "Risque faible", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  moyen: { label: "Risque moyen", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  eleve: { label: "Risque élevé", cls: "bg-red-50 text-red-700 border-red-200" },
};
const norm = (s) => String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();

// Code de mission avec son libellé et son niveau de risque au survol.
// Badge discret (neutre, sans fond de couleur) ; un code absent des
// Paramètres est seulement plus pâle, le détail est dans l'infobulle.
function BadgeCode({ code, entree, onRetirer }) {
  const niveau = NIVEAUX[entree?.niveau];
  return (
    <span className="group relative inline-flex">
      <span
        className={`inline-flex items-center gap-0.5 rounded border border-slate-200 bg-white px-1 font-mono text-[10px] font-medium leading-4 ${
          entree ? "text-slate-500" : "text-slate-400"
        }`}
      >
        {code}
        {onRetirer && (
          <button type="button" onClick={onRetirer} className="text-slate-400 hover:text-red-600" aria-label={`Retirer ${code}`}>
            <X className="h-2.5 w-2.5" />
          </button>
        )}
      </span>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-0 z-30 mb-1.5 hidden w-64 rounded-lg bg-slate-900 px-3 py-2 text-[11px] leading-snug text-white shadow-lg group-hover:block"
      >
        <span className="font-mono font-semibold">{code}</span>
        {entree ? ` — ${entree.libelle}` : " — absent des Paramètres KYC/LAB"}
        {entree?.etat === "archive" && <span className="block text-amber-300">Mission archivée dans les Paramètres</span>}
        {niveau && <span className="mt-1 block text-slate-300">{niveau.label}</span>}
      </span>
    </span>
  );
}

// Recherche par code ou libellé dans la liste des missions, ajout d'un code.
function RechercheCode({ missions, dejaPris, onAjouter, id }) {
  const [q, setQ] = useState("");
  const [ouvert, setOuvert] = useState(false);
  const inputRef = useRef(null);
  const resultats = useMemo(() => {
    const n = norm(q).trim();
    return missions
      .filter((e) => e.etat !== "archive" && !dejaPris.includes(e.code))
      .filter((e) => !n || norm(e.code).includes(n) || norm(e.libelle).includes(n))
      .slice(0, 8);
  }, [q, missions, dejaPris]);
  const ajouter = (code) => {
    onAjouter(code);
    setQ("");
    inputRef.current?.focus();
  };
  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
      <input
        id={id}
        ref={inputRef}
        value={q}
        onChange={(e) => {
          setQ(e.target.value);
          setOuvert(true);
        }}
        onFocus={() => setOuvert(true)}
        onBlur={() => setTimeout(() => setOuvert(false), 150)}
        onKeyDown={(e) => {
          if (e.key === "Enter" && resultats[0]) {
            e.preventDefault();
            ajouter(resultats[0].code);
          }
        }}
        placeholder="Ajouter un code (ex. M008, CSE…)"
        className="w-full rounded-md border border-slate-200 bg-white py-1.5 pl-7 pr-2 text-xs text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-[#9B1B5D]/30"
      />
      {ouvert && resultats.length > 0 && (
        <ul className="absolute left-0 right-0 top-full z-30 mt-1 max-h-60 overflow-y-auto rounded-lg border border-slate-200 bg-white py-1 shadow-lg">
          {resultats.map((e) => {
            const niveau = NIVEAUX[e.niveau];
            return (
              <li key={e.code}>
                <button
                  type="button"
                  onMouseDown={(ev) => {
                    ev.preventDefault();
                    ajouter(e.code);
                  }}
                  className="flex w-full items-start gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-[#9B1B5D]/5"
                >
                  <span className="mt-px font-mono font-semibold text-[#7A1548]">{e.code}</span>
                  <span className="min-w-0 flex-1 text-slate-700">{e.libelle}</span>
                  {niveau && <span className={`shrink-0 rounded-full border px-1.5 text-[10px] ${niveau.cls}`}>{niveau.label.replace("Risque ", "")}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      )}
      {ouvert && q && resultats.length === 0 && (
        <p className="absolute left-0 right-0 top-full z-30 mt-1 rounded-lg border border-slate-200 bg-white px-2.5 py-2 text-xs text-slate-500 shadow-lg">
          Aucune mission ne correspond à « {q} ».
        </p>
      )}
    </div>
  );
}

/**
 * Encadré « Missions récurrentes » : les 3 missions sur toute la largeur.
 * - fiche (lecture) : <MissionsRecurrentes client={client} missions={…} />
 * - formulaire Modifier : + onToggle(champ) et onCodes(champCodes, codes) ;
 * - création (cases gérées par le formulaire) : onCodes seul.
 * peutCoder réserve la modification des codes aux Administrateurs et
 * Experts-comptables.
 */
export default function MissionsRecurrentes({ client, missions = [], onToggle, onCodes, peutCoder = false }) {
  const parCode = useMemo(() => Object.fromEntries(missions.map((e) => [e.code, e])), [missions]);
  const cochable = !!onToggle;
  const codable = !!onCodes && peutCoder;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      {MISSIONS_CODEES.map((m) => {
        const cochee = estCochee(client, m);
        const saisis = codesSaisis(client, m);
        const codes = codesEffectifs(client, m);
        const parDefaut = cochee && !saisis.length;
        return (
          <div
            key={m.champ}
            className={`flex min-w-0 flex-col gap-2 rounded-lg border p-3 transition-colors ${
              cochee ? "border-green-300 bg-green-50/60" : "border-slate-200 bg-white"
            }`}
          >
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
            <button
              type="button"
              disabled={!cochable}
              onClick={() => onToggle?.(m.champ)}
              className={`flex shrink-0 items-center gap-2 text-left text-sm font-semibold text-slate-900 ${cochable ? "cursor-pointer" : "cursor-default"}`}
              aria-pressed={cochee}
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                  cochee ? "border-green-600 bg-green-600 text-white" : "border-slate-300 bg-white"
                }`}
              >
                {cochee && <Check className="h-3 w-3" />}
              </span>
              {m.libelle}
            </button>

            {cochee ? (
              codes.map((code) => (
                <BadgeCode
                  key={code}
                  code={code}
                  entree={parCode[code]}
                  onRetirer={codable && !parDefaut ? () => onCodes?.(m.codes, codes.filter((c) => c !== code)) : null}
                />
              ))
            ) : (
              <span className="text-[11px] text-slate-400">Non confiée</span>
            )}
            </div>

            {codable && cochee && (
              <RechercheCode
                id={`code-${m.codes}`}
                missions={missions}
                dejaPris={codes}
                onAjouter={(code) => onCodes?.(m.codes, [...codes, code])}
              />
            )}
            {!!onCodes && !peutCoder && cochee && (
              <p className="text-[10px] text-slate-400">Codes modifiables par un administrateur ou un expert-comptable.</p>
            )}
          </div>
        );
      })}
    </div>
  );
}
